import { ChangeEvent, useCallback, useMemo, useState } from 'react'
import { t } from '@lingui/macro'
import { useDebounce, useDebouncedCallback } from 'use-debounce'

import { PatientSourceEnum } from '@/core/enums/patient-source.enum'
import { Patient } from '@/core/models/patient.model'
import { useSearchInsIdentityPatientsQuery, useSearchPatientsQuery } from '@/core/services/patient'
import { useIsUS } from '@/hooks/useIsUS'
import { hasValidInsControlKey } from '@/shared/lib/ins-search'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'

import { PatientData } from './usePatientsTableSelection'
import { emptyInsIdentitySearchFilters, InsIdentitySearchErrors, InsIdentitySearchFilters } from './patientDirectory.types'

const rowsPerPageOptions = [5, 10, 15]

function mapPatientToPatientData(patient: Patient, isUS: boolean): PatientData {
  return {
    _id: patient._id,
    name: patient.userInfo.name?.trim() || patient.userInfo.birthName || '',
    surname: patient.userInfo.surname?.trim() || patient.userInfo.birthSurname || '',
    gender: patient.userInfo.gender,
    birthday: patient.userInfo.birthday,
    city: patient.contactInfo?.city,
    secu: (isUS ? patient.primaryPolicy : patient.secu) ?? '',
  }
}

function getInsSearchErrors(filters: InsIdentitySearchFilters): InsIdentitySearchErrors {
  const errors: InsIdentitySearchErrors = {}
  if (filters.birthDate && !filters.birthDate.isValid()) {
    errors.birthDate = t`Enter a valid date using DD/MM/YYYY.`
  }
  if (filters.insNIR && filters.insNIR.length !== 15) {
    errors.insNIR = t`The INS matricule must contain 15 digits.`
  } else if (filters.insNIR && !hasValidInsControlKey(filters.insNIR)) {
    errors.insNIR = t`The INS control key is invalid.`
  }
  return errors
}

export function usePatientDirectorySearch() {
  const isUS = useIsUS()
  const { isINSEnabled: isInsMode } = useIsINSEnabled()
  const [itemsPerPage, setItemsPerPage] = useState(rowsPerPageOptions[0])
  const [page, setPage] = useState(1)
  const [legacySearchValue, setLegacySearchValue] = useState('')
  const [legacySearchArgs, setLegacySearchArgs] = useState({
    limit: rowsPerPageOptions[0],
    skip: 0,
    orderBy: 'userInfo.surname',
    patientSource: PatientSourceEnum.MedconnectPatient,
  })
  const [insFilters, setInsFilters] = useState(emptyInsIdentitySearchFilters)
  const [debouncedInsFilters] = useDebounce(insFilters, 300)

  const insErrors = useMemo(() => getInsSearchErrors(insFilters), [insFilters])
  const hasInsErrors = Boolean(insErrors.birthDate || insErrors.insNIR)

  const debouncedInsErrors = useMemo(() => getInsSearchErrors(debouncedInsFilters), [debouncedInsFilters])
  const hasDebouncedInsErrors = Boolean(debouncedInsErrors.birthDate || debouncedInsErrors.insNIR)

  const legacyQuery = useSearchPatientsQuery(legacySearchArgs, {
    skip: isInsMode,
    refetchOnMountOrArgChange: true,
  })

  const insSearchArgs = useMemo(
    () => ({
      surname: debouncedInsFilters.surname.trim() || undefined,
      givenName: debouncedInsFilters.givenName.trim() || undefined,
      birthDate: debouncedInsFilters.birthDate?.isValid() ? debouncedInsFilters.birthDate.format('YYYY-MM-DD') : undefined,
      insNIR: debouncedInsFilters.insNIR || undefined,
      limit: itemsPerPage,
      skip: (page - 1) * itemsPerPage,
      order: 'ASC' as const,
      orderBy: 'userInfo.birthSurname',
    }),
    [debouncedInsFilters, itemsPerPage, page]
  )

  const insQuery = useSearchInsIdentityPatientsQuery(insSearchArgs, {
    skip: !isInsMode || hasInsErrors || hasDebouncedInsErrors,
    refetchOnMountOrArgChange: true,
  })

  const applyLegacySearch = useDebouncedCallback((value: string, limit: number, skip: number) => {
    setLegacySearchArgs((current) => ({
      ...current,
      limit,
      skip,
      smartSearch: value,
    }))
  }, 300)

  const updateInsFilter = useCallback((patch: Partial<InsIdentitySearchFilters>) => {
    setPage(1)
    setInsFilters((current) => ({ ...current, ...patch }))
  }, [])

  const handleLegacySearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setLegacySearchValue(value)
      setPage(1)
      applyLegacySearch(value, itemsPerPage, 0)
    },
    [applyLegacySearch, itemsPerPage]
  )

  const clearInsFilters = useCallback(() => {
    setPage(1)
    setInsFilters(emptyInsIdentitySearchFilters)
  }, [])

  const changePage = useCallback(
    (nextPage: number) => {
      if (page === nextPage) return
      setPage(nextPage)
      if (!isInsMode) {
        applyLegacySearch.cancel()
        applyLegacySearch(legacySearchValue, itemsPerPage, (nextPage - 1) * itemsPerPage)
      }
    },
    [applyLegacySearch, isInsMode, itemsPerPage, legacySearchValue, page]
  )

  const changeItemsPerPage = useCallback(
    (nextItemsPerPage: number) => {
      if (itemsPerPage === nextItemsPerPage) return
      setPage(1)
      setItemsPerPage(nextItemsPerPage)
      if (!isInsMode) {
        applyLegacySearch.cancel()
        applyLegacySearch(legacySearchValue, nextItemsPerPage, 0)
      }
    },
    [applyLegacySearch, isInsMode, itemsPerPage, legacySearchValue]
  )

  const applySelectedIdentity = useCallback(
    (surname: string, givenName: string) => {
      setPage(1)
      if (isInsMode) {
        setInsFilters({ ...emptyInsIdentitySearchFilters, surname, givenName })
        return
      }
      const value = `${surname} ${givenName}`.trim()
      setLegacySearchValue(value)
      applyLegacySearch.cancel()
      applyLegacySearch(value, itemsPerPage, 0)
    },
    [applyLegacySearch, isInsMode, itemsPerPage]
  )

  const legacyPatients = legacyQuery.data?.items.map((patient) => mapPatientToPatientData(patient, isUS)) ?? []

  return {
    rowsPerPageOptions,
    page,
    itemsPerPage,
    isInsMode,
    legacySearchValue,
    legacyPatients,
    totalLegacyPatients: legacyQuery.data?.totalCount ?? 0,
    isFetchingLegacyPatients: legacyQuery.isFetching,
    legacySearchError: legacyQuery.isError,
    insFilters,
    insErrors,
    insPatients: insQuery.data?.items ?? [],
    totalInsPatients: insQuery.data?.totalCount ?? 0,
    isFetchingInsPatients: insQuery.isFetching,
    insSearchError: insQuery.isError,
    updateInsFilter,
    clearInsFilters,
    handleLegacySearchChange,
    changePage,
    changeItemsPerPage,
    applySelectedIdentity,
  }
}
